import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import {
  Table,
  Checkbox,
  Button,
  message,
  Spin,
  DatePicker,
  Switch,
  Modal,
  Select,
} from "antd";
import moment from "moment";
import { MobileOutlined } from "@ant-design/icons";
import {
  getCampaignClients,
  getMessageTemplates,
  sendCampaign,
} from "../../../api/Services";
import { hasPermission, privilegesEnum } from "../../../helpers/PermissionHelper";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import styles from "./SendCampaign.module.scss";

const SendCampaign: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [type, setType] = useState<"WHATSAPP">("WHATSAPP");
  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduledAt, setScheduledAt] = useState<string | null>(null);
  const [step, setStep] = useState<1 | 2>(1);
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [confirmCountdown, setConfirmCountdown] = useState(5);
  const [confirmDisabled, setConfirmDisabled] = useState(true);
  const confirmTimerRef = useRef<number | null>(null);
  const [searchParams] = useSearchParams();

  const getBasePath = () => {
    if (location.pathname.startsWith("/commercial-intelligence")) {
      return "/commercial-intelligence/campaigns";
    }
    return "/promotions/campaigns";
  };

  useEffect(() => {
    if (!hasPermission(privilegesEnum.SEND_CAMPAIGNS)) {
      message.error(t("Common-NoPermission"));
      navigate(getBasePath(), { replace: true });
    }
  }, [navigate, location.pathname, t]);

  useEffect(() => {
    const typeParam = searchParams.get("type");
    // This page is WhatsApp-focused. If a different type is passed, we ignore it.
    if (typeParam === "WHATSAPP") {
      setType("WHATSAPP");
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchClients = async () => {
      if (!id || !type) return;

      setLoading(true);
      try {
        const response = await getCampaignClients(id, currentPage, { type });
        setClients(response.clients || []);
        setTotalClients(response.totalElements || 0);
      } catch (error) {
        console.error("Error fetching clients:", error);
        message.error(t("Common-ErrorLoadingData"));
      } finally {
        setLoading(false);
      }
    };

    if (id && type) {
      fetchClients();
    } else {
      setClients([]);
      setSelectedClientIds([]);
      setCurrentPage(1);
      setTotalClients(0);
    }
  }, [id, type, currentPage, t]);

  useEffect(() => {
    const fetchTemplates = async () => {
      if (!type || type !== "WHATSAPP") return;
      try {
        const resp = await getMessageTemplates({
          refresh: true,
          page: 0,
          size: 1000,
          status: "APPROVED",
        });
        setTemplates(resp.templates || []);
        // If the selected template is no longer approved, clear selection
        const stillApproved =
            (resp.templates || []).some(
              (tpl: any) =>
                tpl?.id === selectedTemplateId && (tpl?.status || "").toUpperCase() === "APPROVED"
            );
        if (selectedTemplateId && !stillApproved) {
          setSelectedTemplateId(null);
        }
      } catch (e) {
        setTemplates([]);
      }
    };
    fetchTemplates();
  }, [type, selectedTemplateId]);

  // For sending campaigns, we only fetch APPROVED templates from backend.

  const hasValidContact = (client: any) => {
    return (
      (client.mobile && client.mobile.trim() !== "") ||
      (client.phone && client.phone.trim() !== "")
    );
  };

  const handleSelectAll = (e: CheckboxChangeEvent) => {
    if (e.target.checked) {
      const validClientIds = clients
        .filter((client) => hasValidContact(client))
        .map((client) => client.id);
      setSelectedClientIds((prev) =>
        Array.from(new Set([...prev, ...validClientIds]))
      );
    } else {
      const currentPageIds = clients.map((client) => client.id);
      setSelectedClientIds((prev) =>
        prev.filter((id) => !currentPageIds.includes(id))
      );
    }
  };

  const handleSelectClient = (clientId: number, checked: boolean) => {
    const client = clients.find((c) => c.id === clientId);
    if (!client || !hasValidContact(client)) {
      return;
    }
    if (checked) {
      setSelectedClientIds((prev) => [...prev, clientId]);
    } else {
      setSelectedClientIds((prev) => prev.filter((id) => id !== clientId));
    }
  };

  const handleNext = () => {
    if (selectedClientIds.length === 0) {
      message.warning(t("Common-SelectClientsToSend"));
      return;
    }
    setStep(2);
  };

  const openConfirmModal = () => {
    if (!id || selectedClientIds.length === 0) {
      message.warning(t("Common-SelectClientsToSend"));
      return;
    }
    if (scheduleEnabled) {
      if (!scheduledAt) {
        message.warning(t("Common-ScheduledAtRequired"));
        return;
      }
      const scheduleDate = new Date(scheduledAt);
      if (isNaN(scheduleDate.getTime()) || scheduleDate <= new Date()) {
        message.warning(t("Common-ScheduledAtFuture"));
        return;
      }
    }
    if (!selectedTemplateId) {
      message.warning(t("Common-SelectTemplateRequired"));
      return;
    }

    setConfirmVisible(true);
    setConfirmCountdown(5);
    setConfirmDisabled(true);
    if (confirmTimerRef.current) {
      window.clearInterval(confirmTimerRef.current);
    }
    const timerId = window.setInterval(() => {
      setConfirmCountdown((prev) => {
        if (prev <= 1) {
          if (confirmTimerRef.current) {
            window.clearInterval(confirmTimerRef.current);
          }
          setConfirmDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    confirmTimerRef.current = timerId;
  };

  const handleSend = async () => {
    if (!id || selectedClientIds.length === 0) {
      message.warning(t("Common-SelectClientsToSend"));
      return;
    }

    if (scheduleEnabled) {
      if (!scheduledAt) {
        message.warning(t("Common-ScheduledAtRequired"));
        return;
      }
      const scheduleDate = new Date(scheduledAt);
      if (isNaN(scheduleDate.getTime()) || scheduleDate <= new Date()) {
        message.warning(t("Common-ScheduledAtFuture"));
        return;
      }
    }
    if (!selectedTemplateId) {
      message.warning(t("Common-SelectTemplateRequired"));
      return;
    }

    setSending(true);
    try {
      const success = await sendCampaign(
        id,
        selectedClientIds,
        "WHATSAPP",
        scheduleEnabled && scheduledAt ? scheduledAt : undefined,
        undefined,
        selectedTemplateId ?? undefined,
        undefined
      );
      if (success) {
        message.success(t("Common-CampaignSentSuccess"));
        navigate(getBasePath());
      } else {
        message.error(t("Common-CampaignSentError"));
      }
    } catch (error) {
      console.error("Error sending campaign:", error);
      message.error(t("Common-CampaignSentError"));
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setClients([]);
    setSelectedClientIds([]);
    setCurrentPage(1);
    setTotalClients(0);
    setStep(1);
    navigate(getBasePath());
  };

  const validClients = clients.filter((client) => hasValidContact(client));
  const isAllSelected =
    validClients.length > 0 &&
    validClients.every((client) => selectedClientIds.includes(client.id));
  const isIndeterminate =
    validClients.some((client) => selectedClientIds.includes(client.id)) &&
    !isAllSelected;

  const columns = [
    {
      title: t("Common-Name"),
      dataIndex: "name",
      render: (name: string, record: any) => {
        const fullName = [name, record.lastName].filter(Boolean).join(" ");
        const isValid = hasValidContact(record);
        return (
          <span style={{ color: isValid ? "inherit" : "#bfbfbf" }}>
            {fullName || "-"}
          </span>
        );
      },
    },
    {
      title: t("Common-Phone"),
      dataIndex: "mobile",
      render: (value: string, record: any) => {
        const isValid = hasValidContact(record);
        const contactValue = record.mobile || record.phone || "-";
        return (
          <span style={{ color: isValid ? "inherit" : "#bfbfbf" }}>
            {contactValue}
          </span>
        );
      },
    },
  ];

  const selectedTemplate = useMemo(
    () => (templates || []).find((x) => x.id === selectedTemplateId) || null,
    [templates, selectedTemplateId]
  );

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <h1 style={{ margin: 0 }}>{t("Common-SendCampaignWhatsApp", "Enviar campaña por WhatsApp")}</h1>
        <div>
          <Button style={{ marginRight: 8 }} onClick={handleClose}>
            {t("Common-Cancel")}
          </Button>
          {step === 1 && (
            <Button
              type="primary"
              onClick={handleNext}
              disabled={selectedClientIds.length === 0}
            >
              {t("Common-Next")}
            </Button>
          )}
        </div>
      </div>

      {step === 2 && (
        <div className={styles.templateStep}>
          <div className={styles.templateLeft}>
            <div style={{ marginBottom: 16 }}>
              <span style={{ marginRight: 8 }}>{t("Common-ScheduleSend")}:</span>
              <Switch
                checked={scheduleEnabled}
                onChange={(checked) => {
                  setScheduleEnabled(checked);
                  if (!checked) {
                    setScheduledAt(null);
                  }
                }}
              />
              {scheduleEnabled && (
                <DatePicker
                  showTime
                  style={{ marginLeft: 16 }}
                  placeholder={t("Common-SelectScheduleDateTime")}
                  disabledDate={(current) => current && current <= moment().endOf("day")}
                  onChange={(value) => {
                    if (value) {
                      setScheduledAt(value.toISOString());
                    } else {
                      setScheduledAt(null);
                    }
                  }}
                />
              )}
            </div>

            <h3>{t("Common-Template")}</h3>
            <Select
              style={{ width: 360 }}
              placeholder={t("Common-SelectTemplate")}
              value={selectedTemplateId ?? undefined}
              onChange={(val) => setSelectedTemplateId(val)}
              options={(templates || []).map((tpl) => ({
                label: tpl.name,
                value: tpl.id,
              }))}
            />

            <div style={{ marginTop: 16 }}>
              <strong>{t("Common-SelectedCount", { count: selectedClientIds.length })}</strong>
            </div>

            <div style={{ marginTop: 16 }}>
              <Button style={{ marginRight: 8 }} onClick={() => setStep(1)}>
                {t("Common-Back", "Volver")}
              </Button>
              <Button
                type="primary"
                loading={sending}
                onClick={openConfirmModal}
                disabled={selectedClientIds.length === 0 || !selectedTemplateId}
              >
                {scheduleEnabled ? t("Common-Schedule") : t("Common-Send")}
              </Button>
            </div>
          </div>

          <div className={styles.templateRight}>
            <div className={styles.previewPhone}>
              <div className={styles.previewScreen}>
                <div className={styles.whatsappHeader}>
                  <MobileOutlined className={styles.whatsappHeaderIcon} />
                  <span>{t("Common-YourPhoneNumber", "Tu número de teléfono")}</span>
                </div>
                <div className={styles.chatArea}>
                  <div className={styles.bubble}>
                    {selectedTemplate?.body || t("Common-PreviewEmpty", "La vista previa aparecerá aquí.")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {step === 1 && (
        <div>
          <div style={{ marginBottom: 8 }}>
            <Checkbox
              indeterminate={isIndeterminate}
              checked={isAllSelected}
              onChange={handleSelectAll}
            >
              {t("Common-SelectAll")}
            </Checkbox>
            <span style={{ marginLeft: 16 }}>
              {t("Common-SelectedCount", { count: selectedClientIds.length })}
            </span>
          </div>
          <Spin spinning={loading}>
            <Table
              rowSelection={{
                selectedRowKeys: selectedClientIds,
                onSelect: (record, selected) => {
                  handleSelectClient(record.id, selected);
                },
                onSelectAll: (selected) => {
                  if (selected) {
                    const validClientIds = clients
                      .filter((client) => hasValidContact(client))
                      .map((client) => client.id);
                    setSelectedClientIds((prev) =>
                      Array.from(new Set([...prev, ...validClientIds]))
                    );
                  } else {
                    const currentPageIds = clients.map((client) => client.id);
                    setSelectedClientIds((prev) =>
                      prev.filter((id) => !currentPageIds.includes(id))
                    );
                  }
                },
                getCheckboxProps: (record) => ({
                  disabled: !hasValidContact(record),
                }),
              }}
              columns={columns}
              dataSource={clients}
              rowKey="id"
              rowClassName={(record) =>
                !hasValidContact(record) ? styles.disabledRow : ""
              }
              pagination={{
                current: currentPage,
                pageSize: 10,
                total: totalClients,
                onChange: (page) => setCurrentPage(page),
              }}
              size="small"
            />
          </Spin>
        </div>
      )}
      <Modal
        visible={confirmVisible}
        onCancel={() => {
          setConfirmVisible(false);
          if (confirmTimerRef.current) {
            window.clearInterval(confirmTimerRef.current);
          }
        }}
        footer={[
          <Button
            key="back"
            onClick={() => {
              setConfirmVisible(false);
              setStep(1);
            }}
          >
            {t("Common-BackToClients", "Volver a clientes")}
          </Button>,
          <Button
            key="cancel"
            onClick={() => {
              setConfirmVisible(false);
              if (confirmTimerRef.current) {
                window.clearInterval(confirmTimerRef.current);
              }
            }}
          >
            {t("Common-Cancel")}
          </Button>,
          <Button
            key="ok"
            type="primary"
            disabled={confirmDisabled}
            loading={sending}
            onClick={async () => {
              await handleSend();
              setConfirmVisible(false);
            }}
          >
            {confirmDisabled
              ? `${scheduleEnabled ? t("Common-Schedule") : t("Common-Send")} (${confirmCountdown})`
              : scheduleEnabled
                ? t("Common-Schedule")
                : t("Common-Send")}
          </Button>,
        ]}
      >
        <p>
          {t("Common-ConfirmSendToNumbers", { count: selectedClientIds.length })}
        </p>
        <p>{t("Common-Template")}:</p>
        <div
          style={{
            padding: 12,
            border: "1px solid #f0f0f0",
            borderRadius: 4,
            background: "#fafafa",
            whiteSpace: "pre-wrap",
          }}
        >
          {selectedTemplate?.name || "-"}
        </div>

        <div style={{ marginTop: 16 }}>
          <div className={styles.previewPhone}>
            <div className={styles.previewScreen}>
              <div className={styles.whatsappHeader}>
                <MobileOutlined className={styles.whatsappHeaderIcon} />
                <span>{t("Common-YourPhoneNumber", "Tu número de teléfono")}</span>
              </div>
              <div className={styles.chatArea}>
                <div className={styles.bubble}>
                  {selectedTemplate?.body ||
                    t("Common-PreviewEmpty", "La vista previa aparecerá aquí.")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SendCampaign;
